/**
 * Vue Router configuration.
 *
 * @module router
 */

import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

/**
 * Route definitions.
 */
const routes: RouteRecordRaw[] = [
  // Landing
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/LandingView.vue'),
    meta: { title: 'Accueil' },
  },
  // Projects
  {
    path: '/projects',
    name: 'projects',
    component: () => import('@/views/ProjectsView.vue'),
    meta: { title: 'Projets', requiresAuth: true },
  },
  {
    path: '/projects/new',
    name: 'projects-new',
    component: () => import('@/views/EditView.vue'),
    meta: { title: 'Nouveau dossier', requiresAuth: true },
  },
  {
    path: '/projects/:id',
    name: 'projects-view',
    component: () => import('@/views/SynthesisView.vue'),
    meta: { title: 'Synthèse', requiresAuth: true },
  },
  {
    path: '/projects/:id/edit',
    name: 'projects-edit',
    component: () => import('@/views/EditView.vue'),
    meta: { title: 'Modifier', requiresAuth: true },
  },
  {
    path: '/projects/:id/admin',
    name: 'projects-admin',
    component: () => import('@/views/AdminView.vue'),
    meta: { title: 'Administratif', requiresAuth: true },
  },
  {
    path: '/projects/:id/population',
    name: 'projects-population',
    component: () => import('@/views/PopulationView.vue'),
    meta: { title: 'Population', requiresAuth: true },
  },
  {
    path: '/projects/:id/market',
    name: 'projects-market',
    component: () => import('@/views/MarcheView.vue'),
    meta: { title: 'Marché', requiresAuth: true },
  },
  {
    path: '/projects/:id/environment',
    name: 'projects-environment',
    component: () => import('@/views/EnvironmentView.vue'),
    meta: { title: 'Environnement', requiresAuth: true },
  },
  {
    path: '/projects/:id/urban-planning',
    name: 'projects-urban-planning',
    component: () => import('@/views/UrbanismeView.vue'),
    meta: { title: 'Urbanisme', requiresAuth: true },
  },
  {
    path: '/projects/:id/amenities',
    name: 'projects-amenities',
    component: () => import('@/views/AmenitiesView.vue'),
    meta: { title: 'Commodités', requiresAuth: true },
  },
  {
    path: '/projects/:id/long-term-rental',
    name: 'projects-long-term-rental',
    component: () => import('@/views/LocationLongueView.vue'),
    meta: { title: 'Location longue', requiresAuth: true },
  },
  {
    path: '/projects/:id/short-term-rental',
    name: 'projects-short-term-rental',
    component: () => import('@/views/LocationCourteView.vue'),
    meta: { title: 'Location courte', requiresAuth: true },
  },
  {
    path: '/projects/:id/financing',
    name: 'projects-financing',
    component: () => import('@/views/FinancementView.vue'),
    meta: { title: 'Financement', requiresAuth: true },
  },
  {
    path: '/projects/:id/economic',
    name: 'projects-economic',
    component: () => import('@/views/EconomicView.vue'),
    meta: { title: 'Tissu économique', requiresAuth: true },
  },
  // Debug
  {
    path: '/debug/:id/prompt',
    name: 'debug-prompt',
    component: () => import('@/views/DebugPromptView.vue'),
    meta: { title: 'Prompt', requiresAuth: true },
  },
  {
    path: '/debug/:id/logs',
    name: 'debug-logs',
    component: () => import('@/views/DebugLogsView.vue'),
    meta: { title: 'Logs', requiresAuth: true },
  },
  {
    path: '/debug/:id/source',
    name: 'debug-source',
    component: () => import('@/views/DebugSourceView.vue'),
    meta: { title: 'Source', requiresAuth: true },
  },
  {
    path: '/debug/auth',
    name: 'debug-auth',
    component: () => import('@/views/DebugAuthView.vue'),
    meta: { title: 'Auth', requiresAuth: true },
  },
  // Not found
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Page non trouvée' },
  },
]

/**
 * Router instance.
 */
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

// Update document title and handle auth redirects
router.beforeEach((to) => {
  const title = to.meta.title as string | undefined
  document.title = title ? `${title} - Granite` : 'Granite'

  // Redirect to / if not authenticated
  if (to.meta.requiresAuth) {
    // Check for access token or refresh token (can refresh if access expired)
    const accessToken = sessionStorage.getItem('org.cylian.granite.access_token')
    const refreshToken = localStorage.getItem('org.cylian.granite.refresh_token')
    if (!accessToken && !refreshToken) {
      // Completed
      return { name: 'home' }
    }
  }
  // Completed
})

// Completed
export default router
